import { useState, useCallback, useRef } from 'react'
import {
  Title,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Switch,
  Text,
  Alert,
  Loader,
  Center,
  Select,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconPencil, IconTrash, IconPlus, IconAlertCircle } from '@tabler/icons-react'
import { useClientsStore } from '../../stores/clientsStore'
import { useApplicationsStore } from '../../stores/applicationsStore'
import { Client, CreateClientData, UpdateClientData } from '../../api/clientsApi'
import { ClientForm } from './ClientForm'

const TYPE_LABELS: Record<string, string> = {
  studio: 'Studio',
  formatPartner: 'Format Partner',
}

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'studio', label: 'Studios' },
  { value: 'formatPartner', label: 'Format Partners' },
]

export function ClientsView() {
  const {
    clients,
    isLoading,
    error,
    duplicateWarning,
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
    clearError,
    clearDuplicateWarning,
  } = useClientsStore()

  const { applications, fetchApplications } = useApplicationsStore()

  const [showInactive, setShowInactive] = useState(false)
  const [typeFilter, setTypeFilter] = useState('')
  const [formOpened, setFormOpened] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)

  // Fetch helper — builds params from current or overridden filter values
  const doFetch = useCallback(
    (overrides?: { showInactive?: boolean; typeFilter?: string }) => {
      const inactive = overrides?.showInactive ?? showInactive
      const type = overrides?.typeFilter ?? typeFilter
      const params: Record<string, string> = {}
      if (!inactive) params.isActive = 'true'
      if (type) params.type = type
      fetchClients(params)
    },
    [showInactive, typeFilter, fetchClients]
  )

  // Initial fetch on mount
  const didMount = useRef(false)
  if (!didMount.current) {
    didMount.current = true
    fetchClients({ isActive: 'true' })
    fetchApplications()
  }

  const handleCreate = () => {
    setEditingClient(null)
    setFormOpened(true)
  }

  const handleEdit = (client: Client) => {
    setEditingClient(client)
    setFormOpened(true)
  }

  const handleFormSubmit = async (data: CreateClientData | UpdateClientData): Promise<boolean> => {
    if (editingClient) {
      const success = await updateClient(editingClient._id, data as UpdateClientData)
      if (success) {
        notifications.show({ title: 'Success', message: 'Client updated', color: 'green' })
      }
      return success
    } else {
      const result = await createClient(data as CreateClientData)
      if (!result) return false
      if (result.created) {
        notifications.show({ title: 'Success', message: 'Client created', color: 'green' })
        return true
      }
      // Duplicate warning — form will show it, don't close
      return false
    }
  }

  const handleDelete = (client: Client) => {
    modals.openConfirmModal({
      title: 'Deactivate Client',
      children: (
        <Text size="sm">
          Are you sure you want to deactivate <strong>{client.name}</strong>?
        </Text>
      ),
      labels: { confirm: 'Deactivate', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        const success = await deleteClient(client._id)
        if (success) {
          notifications.show({ title: 'Deactivated', message: `${client.name} has been deactivated`, color: 'green' })
        }
      },
    })
  }

  const rows = clients.map((client) => {
    const externalIdCount = Object.keys(client.externalIds ?? {}).length
    return (
    <Table.Tr key={client._id}>
      <Table.Td>{client.name}</Table.Td>
      <Table.Td>
        <Badge variant="light" color={client.type === 'studio' ? 'blue' : 'violet'}>
          {TYPE_LABELS[client.type] || client.type}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {client.aliases.length > 0 ? client.aliases.join(', ') : '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        {externalIdCount > 0 ? (
          <Badge variant="light" color="gray" size="sm">
            {externalIdCount} ID{externalIdCount !== 1 ? 's' : ''}
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">—</Text>
        )}
      </Table.Td>
      <Table.Td>
        <Badge color={client.isActive ? 'green' : 'gray'} variant="light">
          {client.isActive ? 'Active' : 'Inactive'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <ActionIcon variant="subtle" onClick={() => handleEdit(client)}>
            <IconPencil size={16} />
          </ActionIcon>
          {client.isActive && (
            <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(client)}>
              <IconTrash size={16} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  )})

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Clients</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
          New Client
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" onClose={clearError} withCloseButton>
          {error}
        </Alert>
      )}

      <Group>
        <Select
          placeholder="Filter by type"
          data={TYPE_FILTER_OPTIONS}
          value={typeFilter}
          onChange={(v) => {
            const val = v || ''
            setTypeFilter(val)
            doFetch({ typeFilter: val })
          }}
          clearable={false}
          w={180}
        />
        <Switch
          label="Show inactive"
          checked={showInactive}
          onChange={(e) => {
            const val = e.target.checked
            setShowInactive(val)
            doFetch({ showInactive: val })
          }}
        />
      </Group>

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : clients.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No clients found. Create your first client to get started.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Aliases</Table.Th>
              <Table.Th>External IDs</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      )}

      <ClientForm
        opened={formOpened}
        onClose={() => {
          setFormOpened(false)
          clearDuplicateWarning()
        }}
        onSubmit={handleFormSubmit}
        client={editingClient}
        duplicateWarning={duplicateWarning}
        onClearDuplicateWarning={clearDuplicateWarning}
        applications={applications}
      />
    </Stack>
  )
}
